/**
 * RegisterPage Component
 *
 * User registration page with form validation and terms acceptance.
 * Creates new user account with automatic 'pro' plan (14-day free trial) and
 * redirects to email verification.
 *
 * Flow:
 * 1. User fills in registration form (name, email, company, account type, password)
 * 2. Account created with automatic 'pro' plan — 14-day free trial, no payment required
 * 3. Verification email sent; user redirected to /verify-email
 * 4. After verification and login, user can use the product
 *
 * Edge cases handled:
 * - Email already registered + verified → inline error with "Log in instead?" link
 * - Email already registered but NOT verified → backend resends verification email;
 *   frontend redirects to /verify-email with a "resent" notice
 *
 * Usage:
 *   <Route path="/register" element={<RegisterPage />} />
 */

import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { AuthShell, AuthTitle, AuthField, AuthSubmit, AuthNote, authLink } from '@/components/auth/AuthShell';
import { useAuth } from '@/hooks/useAuth';

const ACCOUNT_TYPE_OPTIONS = [
  { value: 'agency', label: 'Agency or consultant', sub: 'For clients' },
  { value: 'brand', label: 'Brand or in-house', sub: 'Own company' },
  { value: 'freelancer', label: 'Freelancer', sub: 'Mix of both' },
] as const;

export const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  // Invitation link: /register?invite=…&email=… — sign up with that email to join the business.
  const [params] = useSearchParams();
  const invitedEmail = params.get('invite') ? (params.get('email') || '') : '';

  const [formData, setFormData] = useState({
    name: '',
    email: invitedEmail,
    password: '',
    confirmPassword: '',
    company: '',
    accountType: '',
  });

  const [errors, setErrors] = useState<{
    name?: string;
    email?: string;
    password?: string;
    confirmPassword?: string;
    company?: string;
    accountType?: string;
    terms?: string;
    general?: string;
  }>({});

  const [loading, setLoading] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showLoginLink, setShowLoginLink] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error for this field
    if (errors[name as keyof typeof errors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
    if (name === 'email') setShowLoginLink(false);
  };

  const validateForm = (): boolean => {
    const newErrors: typeof errors = {};

    // Name validation
    if (!formData.name) {
      newErrors.name = 'Full name is required';
    } else if (formData.name.length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }

    // Email validation
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Account type validation
    if (!formData.accountType) {
      newErrors.accountType = 'Please select how you primarily use this';
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      newErrors.password = 'Password must contain uppercase, lowercase, and number';
    }

    // Confirm password validation
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    // Company validation (optional but if provided must be valid)
    if (formData.company && formData.company.length < 2) {
      newErrors.company = 'Company name must be at least 2 characters';
    }

    // Terms acceptance
    if (!acceptedTerms) {
      newErrors.terms = 'You must accept the terms and conditions';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    setShowLoginLink(false);

    if (!validateForm()) {
      return;
    }

    await handleRegistrationSubmit();
  };

  const handleRegistrationSubmit = async () => {
    try {
      setLoading(true);

      const result = await register({
        name: formData.name,
        email: formData.email,
        password: formData.password,
        company: formData.company || undefined,
        accountType: formData.accountType,
        termsAccepted: acceptedTerms,
      }) as any;

      // Backend resent verification email for a pending account
      if (result?.code === 'VERIFICATION_PENDING') {
        navigate(`/verify-email?email=${encodeURIComponent(formData.email)}`, {
          state: { email: formData.email, resent: true },
          replace: true,
        });
        return;
      }

      // Normal successful registration
      const skipEmailVerification = import.meta.env.VITE_SKIP_EMAIL_VERIFICATION === 'true';

      if (skipEmailVerification) {
        navigate('/login', {
          state: {
            message: 'Registration successful! Please sign in with your credentials.',
            email: formData.email,
          },
          replace: true,
        });
      } else {
        navigate(`/verify-email?email=${encodeURIComponent(formData.email)}`, {
          state: { email: formData.email },
          replace: true,
        });
      }
    } catch (err: any) {
      console.error('Registration error:', err);

      const status = err.response?.status;

      if (status === 409) {
        // Email belongs to an already-verified account
        setErrors({ email: 'An account with this email already exists.' });
        setShowLoginLink(true);
      } else {
        setErrors({
          general: err.message || 'Registration failed. Please try again.',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell wideCard headline={<>Your next month of ads, planned for you.</>} sub="Tell us about the business, and Admeru plans, makes and runs the campaigns. Nothing is spent until you approve.">
      <AuthTitle sub={invitedEmail ? 'You were invited to a business on Admeru. Sign up with this email and you’ll join it once your email is confirmed.' : undefined}>
        {invitedEmail ? 'Accept your invitation' : 'Create an account'}
      </AuthTitle>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
        {errors.general && <AuthNote>{errors.general}</AuthNote>}
        <AuthField label="Full name" name="name" value={formData.name} onChange={handleChange} placeholder="Your name" error={errors.name} autoComplete="name" autoFocus />
        <AuthField label="Email" name="email" type="email" value={formData.email} onChange={handleChange} placeholder="you@example.com" autoComplete="email"
          error={errors.email && <>{errors.email}{showLoginLink && <> <Link to="/login" className={authLink}>Sign in instead</Link></>}</>} />
        <AuthField label="Company (optional)" name="company" value={formData.company} onChange={handleChange} placeholder="Your business" error={errors.company} autoComplete="organization" />
        <fieldset className="m-0 flex flex-col gap-1.5 border-0 p-0">
          <legend className="mb-1.5 p-0 text-sm text-[#4A5468]">How you’ll use Admeru</legend>
          <div className="grid grid-cols-3 gap-2">
            {ACCOUNT_TYPE_OPTIONS.map(opt => {
              const on = formData.accountType === opt.value;
              return (
                <button key={opt.value} type="button" aria-pressed={on}
                  onClick={() => { setFormData(prev => ({ ...prev, accountType: opt.value })); setErrors(prev => ({ ...prev, accountType: undefined })); }}
                  className={`flex flex-col items-start gap-0.5 rounded-[12px] px-3 py-2.5 text-left transition-colors ${on ? 'bg-[#0F1526] text-white' : 'bg-[#F1F2F6] text-[#0F1526] hover:bg-[#E6E8EF]'}`}>
                  <span className="text-sm font-semibold leading-tight">{opt.label}</span>
                  <span className={`text-xs ${on ? 'text-[#C9CEDB]' : 'text-[#646D80]'}`}>{opt.sub}</span>
                </button>
              );
            })}
          </div>
          {errors.accountType && <p className="m-0 text-sm font-semibold text-[#B42318]">{errors.accountType}</p>}
        </fieldset>
        <div className="grid gap-4 sm:grid-cols-2">
          <AuthField label="Password" name="password" type="password" value={formData.password} onChange={handleChange} placeholder="••••••••" error={errors.password} autoComplete="new-password" hint="8+ characters, with upper and lower case and a number" />
          <AuthField label="Password again" name="confirmPassword" type="password" value={formData.confirmPassword} onChange={handleChange} placeholder="••••••••" error={errors.confirmPassword} autoComplete="new-password" />
        </div>
        <p className="m-0 rounded-[12px] bg-[#F1F2F6] px-4 py-3 text-xs leading-relaxed text-[#4A5468]">
          Admeru collects your name, email and ad account tokens to plan and publish your campaigns. You can withdraw consent and ask us to delete your data at any time: <a href="mailto:privacy@admeru.com" className={authLink}>privacy@admeru.com</a>.
        </p>
        <label className="flex cursor-pointer items-start gap-2.5 text-sm text-[#2B3346]">
          <input type="checkbox" checked={acceptedTerms} className="mt-0.5 h-[18px] w-[18px] shrink-0 accent-[#0F1526]"
            onChange={(e) => { setAcceptedTerms(e.target.checked); if (errors.terms) setErrors(prev => ({ ...prev, terms: undefined })); }} />
          <span>I agree to the <a href="/terms" className={authLink}>Terms</a> and the <a href="/privacy" className={authLink}>Privacy policy</a>.</span>
        </label>
        {errors.terms && <p className="m-0 -mt-2 text-sm font-semibold text-[#B42318]">{errors.terms}</p>}
        <AuthSubmit loading={loading}>Create account</AuthSubmit>
        <p className="m-0 text-center text-sm text-[#4A5468]">Already have an account? <Link to="/login" className={authLink}>Sign in</Link></p>
      </form>
    </AuthShell>
  );
};

export default RegisterPage;
